import { useQuery } from '@tanstack/react-query';
import { FRAUD_DETECTOR_JOB_API } from '@/pages/ats-job-creation/tabs/ai-teammates/api/fraudDetector.api';
import { fraudDetectorTeammateKeys } from '@/pages/ats-job-creation/tabs/ai-teammates/hooks/useFraudDetectorTeammate';

type AnyRecord = Record<string, any> | null | undefined;

// fraud_ratings that map to no_issues, inconclusive, or suspicious
const SHOW_WITHOUT_FLAGS_RATINGS = new Set([
  'NO_ISSUES',
  'SAFE',
  'LOW_CONFIDENCE',
  'SUSPICIOUS',
  'HIGH_RISK',
  'HIGH_CONFIDENCE',
  'INCONCLUSIVE',
  'MEDIUM_CONFIDENCE',
]);

const SHOW_WITHOUT_FLAGS_STATUSES = new Set([
  'no_issues',
  'inconclusive',
  'suspicious',
]);

export const hasCbvResultToDisplay = (cbv: AnyRecord): boolean => {
  if (!cbv) return false;
  const { fraud_rating, cbv_status } = cbv;
  if (fraud_rating) return SHOW_WITHOUT_FLAGS_RATINGS.has(fraud_rating);
  return SHOW_WITHOUT_FLAGS_STATUSES.has(cbv_status);
};

export interface FraudDetectorActionState {
  disabled: boolean;
  tooltip: string;
}

const disabled = (tooltip: string): FraudDetectorActionState => ({
  disabled: true,
  tooltip,
});

export const isFraudDetectionIntegrationEnabled = (jobSettings: AnyRecord) =>
  jobSettings?.contact_background_verification_integration_enabled === true;

export const hasContactEmail = (applicationData: AnyRecord) =>
  !!applicationData?.contact?.email?.trim();

export const hasResume = (applicationData: AnyRecord) =>
  applicationData?.resume !== null && applicationData?.resume !== undefined;

export const isCbvInProgress = (applicationData: AnyRecord) =>
  applicationData?.contact_background_verification?.cbv_status ===
  'in_progress';

export const isCbvAlreadyVerified = (applicationData: AnyRecord) => {
  const cbv = applicationData?.contact_background_verification;
  if (!cbv) return false;
  const { cbv_status, fraud_rating } = cbv;
  if (fraud_rating) return true;
  return (
    cbv_status !== null &&
    cbv_status !== undefined &&
    cbv_status !== 'in_progress'
  );
};

export const hasValidPhoneWithCountryCode = (
  phone: string | null | undefined,
  phoneCode?: string | null
) => {
  const code = phoneCode?.trim() ?? '';
  const number = phone?.trim() ?? '';
  const combined = code ? `${code}${number}` : number;
  return /^\+\d/.test(combined);
};

export const getFraudDetectorActionState = (
  applicationData: AnyRecord,
  jobSettings: AnyRecord
): FraudDetectorActionState => {
  if (!isFraudDetectionIntegrationEnabled(jobSettings))
    return disabled('Fraud detector is not enabled for this job');

  if (isCbvInProgress(applicationData))
    return disabled(
      'Fraud detection is already in progress for this candidate'
    );

  if (isCbvAlreadyVerified(applicationData))
    return disabled('Fraud detection has already been run for this candidate');

  if (!hasContactEmail(applicationData))
    return disabled('An email address is required to run fraud detection');

  if (!hasResume(applicationData))
    return disabled('A resume is required to run fraud detection');

  if (
    !hasValidPhoneWithCountryCode(
      applicationData?.contact?.phone_number,
      applicationData?.contact?.phone_code
    )
  )
    return disabled(
      'A valid phone number with a country code is required for fraud detection'
    );

  return { disabled: false, tooltip: '' };
};

const useJobSettings = (jobId: string | number | undefined, enabled: boolean) =>
  useQuery({
    queryKey: fraudDetectorTeammateKeys.jobSettings(jobId as string | number),
    queryFn: () =>
      FRAUD_DETECTOR_JOB_API.getJobSettings(jobId as string | number),
    enabled: enabled && !!jobId,
    staleTime: 30 * 60 * 1000, // 30 minutes
  }).data;

export const useFraudDetectionJobEnabled = (
  jobId: string | number | undefined,
  enabled: boolean
): boolean => {
  const jobSettings = useJobSettings(jobId, enabled);
  if (!enabled || !jobSettings) return false;
  return isFraudDetectionIntegrationEnabled(jobSettings);
};

export const useFraudDetectorActionState = (
  applicationData: AnyRecord,
  enabled: boolean
): FraudDetectorActionState => {
  const jobSettings = useJobSettings(applicationData?.ats_job?.id, enabled);
  if (!enabled) return { disabled: false, tooltip: '' };
  return getFraudDetectorActionState(applicationData, jobSettings);
};
